/**
 * Education helpers for Review Request
 */

export const EDUCATION_QUALIFICATION_LEVELS = [
  { key: "doctorate", label: "Doctorate" },
  { key: "post_graduate", label: "Post Graduate" },
  { key: "graduate", label: "Graduate" },
  { key: "diploma", label: "Diploma" },
  { key: "12th", label: "12th" },
  { key: "10th", label: "10th" },
];

const QUALIFICATION_ALIASES = {
  "10th": "10th",
  tenth: "10th",
  "12th": "12th",
  twelfth: "12th",
  diploma: "diploma",
  graduate: "graduate",
  graduation: "graduate",
  "post graduate": "post_graduate",
  "post-graduate": "post_graduate",
  postgraduate: "post_graduate",
  post_graduate: "post_graduate",
  doctorate: "doctorate",
  phd: "doctorate",
};

export const normalizeEducationQualification = (value) => {
  if (!value) return "";
  const raw = String(value).trim().toLowerCase();
  return QUALIFICATION_ALIASES[raw] || raw.replace(/\s+/g, "_");
};

export const getEducationQualificationLabel = (qualification) => {
  const key = normalizeEducationQualification(qualification);
  return (
    EDUCATION_QUALIFICATION_LEVELS.find((item) => item.key === key)?.label ||
    String(qualification || "")
  );
};

/** Degree / Diploma / Domain field label. null = hide field. */
export const getEducationDegreeLabel = (qualification) => {
  const key = normalizeEducationQualification(qualification);
  if (key === "10th" || key === "12th") return null;
  if (key === "diploma") return "Diploma";
  if (key === "doctorate") return "Domain";
  return "Degree";
};

/** University / Board / Affiliating Body field label. */
export const getEducationUniversityLabel = (qualification) => {
  const key = normalizeEducationQualification(qualification);
  if (key === "10th" || key === "12th") return "Board";
  if (key === "diploma") return "Affiliating Body";
  return "University";
};

export const getEducationQualificationRank = (qualification) => {
  const key = normalizeEducationQualification(qualification);
  const index = EDUCATION_QUALIFICATION_LEVELS.findIndex(
    (item) => item.key === key,
  );
  return index === -1 ? EDUCATION_QUALIFICATION_LEVELS.length : index;
};

/**
 * Highest qualification first then remaining in descending order.
 */
export const sortEducationRecords = (records = []) => {
  return [...records].sort(
    (a, b) =>
      getEducationQualificationRank(a.qualification) -
      getEducationQualificationRank(b.qualification),
  );
};

export const EDUCATION_RECORD_FIELD_KEYS = [
  "degree",
  "start_date",
  "graduation_date",
  "university",
  "college",
  "cgpa_percentage",
];

/**
 * Build visible field defs for one qualification record.
 */
export const getEducationRecordFields = (record) => {
  const qualification = record?.qualification;
  const degreeLabel = getEducationDegreeLabel(qualification);
  const universityLabel = getEducationUniversityLabel(qualification);

  const fields = [
    { key: "qualification", label: "Qualification", isRequired: true },
  ];
  if (degreeLabel) {
    fields.push({
      key: "degree",
      label: degreeLabel,
      isRequired: true,
    });
  }
  fields.push(
    { key: "start_date", label: "Start Date", isRequired: true },
    { key: "graduation_date", label: "Graduation Date", isRequired: true },
    { key: "university", label: universityLabel, isRequired: true },
    {
      key: "college",
      label: isSchoolLevelEducationQualification(qualification)
        ? "School"
        : "College/Institute",
    },
    { key: "cgpa_percentage", label: "CGPA/Percentage" },
    { key: "maximum_cgpa_percentage", label: "Maximum CGPA/Percentage" },
  );
  return fields;
};

/**
 * School-level (10th / 12th) use marksheet + certificate.
 * Higher levels use consolidated_marksheet + graduation_certificate.
 */
export const isSchoolLevelEducationQualification = (qualification) => {
  const key = normalizeEducationQualification(qualification);
  return key === "10th" || key === "12th";
};

/**
 * Canonical Onboarding field_name for an education document kind
 * ("marksheet" | "certificate") based on the record's qualification.
 */
export const getEducationDocumentCanonicalFieldName = (
  docKind,
  qualification,
) => {
  const kind = String(docKind || "")
    .trim()
    .toLowerCase();
  const isSchoolLevel = isSchoolLevelEducationQualification(qualification);

  if (kind === "marksheet") {
    return isSchoolLevel ? "marksheet" : "consolidated_marksheet";
  }
  if (kind === "certificate") {
    return isSchoolLevel ? "certificate" : "graduation_certificate";
  }
  return "";
};

/**
 * Display labels for education documents, matching Onboarding naming.
 */
export const getEducationDocumentLabels = (qualification) => {
  const isSchoolLevel = isSchoolLevelEducationQualification(qualification);
  const qualificationLabel =
    getEducationQualificationLabel(qualification) || "Education";

  if (isSchoolLevel) {
    return {
      marksheet: `${qualificationLabel} Marksheet.pdf`,
      certificate: `${qualificationLabel} Certificate.pdf`,
    };
  }

  return {
    marksheet: `${qualificationLabel} Consolidated Marksheet.pdf`,
    certificate: `${qualificationLabel} Graduation Certificate.pdf`,
  };
};

const EDUCATION_MASTER_CACHE_MS = 60_000;
const educationMasterCache = new Map();

const normalizeEducationMasterItem = (item) => {
  if (typeof item === "string") return item.trim();
  if (!item || typeof item !== "object") return "";
  const value = item.name ?? item.value ?? item.label ?? "";
  return typeof value === "string" ? value.trim() : "";
};

export const normalizeEducationMasterItems = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data.map(normalizeEducationMasterItem).filter(Boolean);
  }
  if (typeof data === "object") {
    const nested = data.items ?? data.results;
    if (Array.isArray(nested)) {
      return nested.map(normalizeEducationMasterItem).filter(Boolean);
    }
  }
  return [];
};

const dedupeEducationMasterOptions = (options) => {
  const seen = new Set();
  const deduped = [];
  options.forEach((option) => {
    const normalized = String(option || "").trim();
    const key = normalized.toLowerCase();
    if (!normalized || seen.has(key)) return;
    seen.add(key);
    deduped.push(normalized);
  });
  return deduped;
};

export const rankEducationMasterOptions = (options) =>
  dedupeEducationMasterOptions(options).sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: "base" }),
  );

/**
 * Short-lived cache for education master lookups (UI helper, not Redux state).
 */
export const withEducationMasterCache = (cacheKey, loader) => {
  const cached = educationMasterCache.get(cacheKey);
  if (cached && cached.expires > Date.now()) {
    return cached.promise;
  }

  const promise = Promise.resolve().then(loader);
  educationMasterCache.set(cacheKey, {
    expires: Date.now() + EDUCATION_MASTER_CACHE_MS,
    promise,
  });

  return promise.catch((error) => {
    educationMasterCache.delete(cacheKey);
    throw error;
  });
};

export const hasExactEducationMasterMatch = (options, search) =>
  (options || []).some(
    (option) =>
      option.trim().toLowerCase() ===
      String(search || "").trim().toLowerCase(),
  );
